import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Icon from "@mui/material/Icon";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useMaterialUIController, setModuloActivo } from "context";
import { FP } from "components/financepro";
import DesktopSoftwareUpdateModal, {
  shouldShowDesktopUpdateModal,
} from "examples/DesktopSoftwareUpdateModal";
import routesFac from "routesfac";
import routesFiscal from "routesfiscal";
import routesCont from "routescont";
import routesNom from "routesnom";

/** Clave en minúsculas → icono Material, nombre para mostrar y texto de descripción. */
const MODULOS_META = {
  prestamos: {
    icon: "two_wheeler",
    nombre: "Motocicletas",
    descripcion: "Cartera, préstamos y cancelaciones",
  },
  gestionfiscal: {
    icon: "request_quote",
    nombre: "Fiscal",
    descripcion: "Gestión fiscal de la empresa",
  },
  fiscal: {
    icon: "request_quote",
    nombre: "Fiscal",
    descripcion: "Gestión fiscal de la empresa",
  },
  facturacion: {
    icon: "receipt_long",
    nombre: "Facturación",
    descripcion: "Facturación y Modulo de Ventas",
  },
  contabilidad: {
    icon: "account_balance_wallet",
    nombre: "Contabilidad",
    descripcion: "Catálogo, motor contable y cuentas por pagar",
  },
  cuentasxpagar: {
    icon: "account_balance_wallet",
    nombre: "Contabilidad",
    descripcion: "Catálogo, motor contable y cuentas por pagar",
  },
  nomina: {
    icon: "badge",
    nombre: "Nómina",
    descripcion: "Personal, períodos y pagos",
  },
};

// Devuelve la primera ruta navegable (preferentemente anidada) de un grupo
// de rutas. Si el grupo no tiene `collapse`, retorna su propia `route`.
function obtenerRutaInicio(grupo) {
  if (!grupo) return null;
  if (Array.isArray(grupo.collapse) && grupo.collapse.length > 0) {
    const hijoNavegable = grupo.collapse.find(
      (child) => child?.type === "collapse" && child?.route && child.route !== "/login"
    );
    if (hijoNavegable) return hijoNavegable.route;
  }
  return grupo.route || null;
}

// Recorre un arreglo de rutas (estilo `routes.js` / `routesfac.js`) y devuelve
// la primera ruta navegable real, ignorando entradas compartidas con el menú
// principal (Dashboard) y la pantalla de login. Útil para localizar la ruta
// de inicio de un módulo cuando no existe un grupo padre con un `key`
// específico.
function obtenerPrimeraRutaNavegable(rutas) {
  for (const r of rutas || []) {
    if (!r) continue;
    if (r.route === "/login") continue;
    if (r.key === "dashboard") continue;
    const candidata = obtenerRutaInicio(r);
    if (!candidata || candidata === "/login") continue;
    return candidata;
  }
  return null;
}

// El módulo de préstamos aterriza en el Inicio (accesos rápidos, recientes y
// widgets personales), no en una pantalla de trabajo concreta.
const RUTA_PRESTAMOS_INICIO = "/dashboard";

// Mapa de destinos por módulo. La ruta de Facturación se deriva de
// `routesfac.js`, de modo que al hacer click en su tarjeta se carguen
// efectivamente las rutas definidas en ese archivo.
const RUTA_FISCAL_INICIO =
  obtenerPrimeraRutaNavegable(routesFiscal) || "/fiscal/subircertificado";

const RUTA_CONTABILIDAD_INICIO =
  obtenerPrimeraRutaNavegable(routesCont) || "/contabilidad/catalogodecuentas";

const RUTA_NOMINA_INICIO =
  obtenerPrimeraRutaNavegable(routesNom) || "/nomina/inicio";

const MODULO_DESTINO = {
  prestamos: RUTA_PRESTAMOS_INICIO,
  facturacion: obtenerPrimeraRutaNavegable(routesFac),
  gestionfiscal: RUTA_FISCAL_INICIO,
  fiscal: RUTA_FISCAL_INICIO,
  contabilidad: RUTA_CONTABILIDAD_INICIO,
  cuentasxpagar: RUTA_CONTABILIDAD_INICIO,
  nomina: RUTA_NOMINA_INICIO,
};

export default function PantallaModulosCliente() {
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();
  const { modulos: modulosCliente, nombreNegocio } = controller;
  const [desktopUpdateOpen, setDesktopUpdateOpen] = useState(false);

  useEffect(() => {
    async function checkUpdate() {
      if (await shouldShowDesktopUpdateModal()) {
        setDesktopUpdateOpen(true);
      }
    }
    checkUpdate();
  }, []);

  // Marca el módulo elegido en el contexto (y en localStorage) antes de navegar
  // para que `App.js` decida qué sidebar y arreglo de rutas activar. La clave
  // se guarda en minúsculas para coincidir con `MODULO_DESTINO`.
  const seleccionarModulo = (clave, destino) => {
    if (!destino) return;
    setModuloActivo(dispatch, clave);
    navigate(destino);
  };

  const tarjetas = useMemo(() => {
    if (modulosCliente === undefined || modulosCliente === null || modulosCliente === "false") {
      return [];
    }

    let partes = [];
    if (Array.isArray(modulosCliente)) {
      partes = modulosCliente.map((m) => String(m).trim()).filter(Boolean);
    } else if (typeof modulosCliente === "string") {
      partes = modulosCliente.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
    }

    const slugs = [...new Map(partes.map((p) => [p.toLowerCase(), p])).values()];

    return slugs.map((slug) => {
      const clave = slug.trim().toLowerCase();
      const meta = MODULOS_META[clave];
      const nombre =
        meta?.nombre ?? slug.trim().replace(/\b\w/g, (c) => c.toUpperCase());
      return {
        clave,
        nombre,
        icon: meta?.icon ?? "widgets",
        descripcion: meta?.descripcion ?? "Módulo habilitado según su licencia actual",
      };
    });
  }, [modulosCliente]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        p: { xs: 2.5, sm: 4, md: 6 },
        background: `radial-gradient(1200px 600px at 80% -10%, rgba(27,108,251,0.10), transparent 60%), linear-gradient(180deg, ${FP.color.surface} 0%, ${FP.color.surfaceAlt} 100%)`,
      }}
    >
      <DesktopSoftwareUpdateModal
        open={desktopUpdateOpen}
        onClose={() => setDesktopUpdateOpen(false)}
      />
      <Box sx={{ maxWidth: 1080, width: "100%", mx: "auto", textAlign: "center" }}>
        <Typography
          variant="overline"
          sx={{
            color: FP.color.textSecondary,
            fontWeight: 700,
            letterSpacing: "0.16em",
            display: "block",
            mb: 1.2,
          }}
        >
          Suite Corporativa · FinancePro
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: FP.color.navy,
            letterSpacing: "-0.025em",
            mb: 1.5,
            fontSize: { xs: "1.6rem", md: "2.2rem" },
          }}
        >
          Módulos disponibles
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: FP.color.textSecondary,
            mb: { xs: 4, md: 5 },
            maxWidth: 580,
            mx: "auto",
            lineHeight: 1.65,
          }}
        >
          {nombreNegocio && nombreNegocio !== "false"
            ? `${nombreNegocio} · seleccione el área de trabajo a operar.`
            : "Seleccione el área de trabajo contratada para su organización."}
        </Typography>

        {tarjetas.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              p: 4,
              textAlign: "center",
              maxWidth: 560,
              mx: "auto",
            }}
          >
            <Typography sx={{ color: FP.color.textSecondary, fontSize: "1rem" }}>
              No hay módulos en la sesión. Inicie sesión nuevamente o contacte a soporte.
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={3} justifyContent="center">
            {tarjetas.map(({ clave, nombre, icon, descripcion }) => {
              const destino = MODULO_DESTINO[clave] || null;
              const clickable = Boolean(destino);
              return (
                <Grid item xs={12} sm={6} md={4} key={clave}>
                  <Card
                    elevation={0}
                    onClick={() => {
                      if (clickable) seleccionarModulo(clave, destino);
                    }}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (!clickable) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        seleccionarModulo(clave, destino);
                      }
                    }}
                    sx={{
                      height: "100%",
                      borderRadius: 2.5,
                      border: `1px solid ${FP.color.border}`,
                      bgcolor: FP.color.cardBg,
                      boxShadow: FP.shadow.soft,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      transition: "all 0.22s ease",
                      ...(clickable
                        ? {
                            cursor: "pointer",
                            "&:focus-visible": {
                              outline: `2px solid ${FP.color.navy}`,
                              outlineOffset: 2,
                            },
                          }
                        : {}),
                      "&:hover": clickable
                        ? {
                            boxShadow: FP.shadow.md,
                            borderColor: FP.color.borderStrong,
                            transform: "translateY(-3px)",
                          }
                        : {},
                    }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        width: "100%",
                        background: `linear-gradient(90deg, ${FP.color.navy} 0%, ${FP.color.accentBlue} 100%)`,
                      }}
                    />
                    <CardContent
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        gap: 2.2,
                        px: 3,
                        py: { xs: 4.5, sm: 5.5 },
                        minHeight: { xs: 280, sm: 300 },
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          bgcolor: FP.color.surfaceAlt,
                          border: `1px solid ${FP.color.border}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: FP.color.navy,
                        }}
                      >
                        <Icon sx={{ fontSize: 32 }}>{icon}</Icon>
                      </Box>
                      <Box sx={{ width: "100%", maxWidth: 280 }}>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: FP.color.navy,
                            fontSize: "1.35rem",
                            letterSpacing: "-0.01em",
                            lineHeight: 1.3,
                            mb: 1.2,
                          }}
                        >
                          {nombre}
                        </Typography>
                        <Typography
                          sx={{
                            color: FP.color.textSecondary,
                            fontWeight: 600,
                            fontSize: "0.78rem",
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            lineHeight: 1.6,
                          }}
                        >
                          {descripcion}
                        </Typography>
                      </Box>
                      {clickable ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.8,
                            px: 1.6,
                            py: 0.6,
                            borderRadius: 1.4,
                            bgcolor: FP.color.navy,
                            color: "#FFFFFF",
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                          }}
                        >
                          Ingresar <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                        </Box>
                      ) : null}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
